import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { updateFileIndex, updateFileIndexType } from "@root/store/slices/entities";
import { isPdfUrlValidThunk } from "@root/store/thunks";
import { validateUrl } from "@root/utils/helpers";
import { PDF } from "@root/webConstants";
import { useMainChatContext } from "@root/context/kps-chat-context";

interface UseKeyboardNavigationProps {
  fileIndex: number;
  noOfDocs: number;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  onSubmit: () => void;
}

/**
 * Custom hook to handle keyboard navigation (arrow keys, enter)
 */
export const useKeyboardNavigation = ({
  fileIndex,
  noOfDocs,
  inputRef,
  onSubmit,
}: UseKeyboardNavigationProps) => {
  const dispatch = useDispatch();
  const {
    urlType,
    setFlagValidateUrl,
    fetchPdfPreview,
    setPdfLinkLoading,
  } = useMainChatContext();

  const onKeyPressHandler = useCallback(
    async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      switch (e.keyCode) {
        case 40: // Down arrow
          if (fileIndex < noOfDocs) {
            dispatch(updateFileIndex(fileIndex + 1));
            dispatch(updateFileIndexType("inc"));
          }
          break;
        case 38: // Up arrow
          if (fileIndex > 1) {
            dispatch(updateFileIndex(fileIndex - 1));
            dispatch(updateFileIndexType("dec"));
          }
          break;
        case 13: // Enter
          if (!e?.shiftKey) {
            e.preventDefault();
            let validate = false;
            const url = inputRef?.current?.value;
            if (urlType?.value === PDF) {
              try {
                setPdfLinkLoading(true);
                const res: any = await dispatch(
                  isPdfUrlValidThunk({ payload: { url } })
                );
                validate = res?.payload?.data;
                fetchPdfPreview({ url });
              } catch (err) {
                validate = false;
              } finally {
                setPdfLinkLoading(false);
              }
            } else {
              validate = Boolean(validateUrl(urlType?.value, url));
            }
            setFlagValidateUrl(validate);
            if (validate) onSubmit();
          }
          break;
      }
    },
    [
      fileIndex,
      noOfDocs,
      inputRef,
      urlType,
      dispatch,
      setFlagValidateUrl,
      fetchPdfPreview,
      setPdfLinkLoading,
      onSubmit,
    ]
  );

  return { onKeyPressHandler };
};
